'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Marquee } from 'components/shared/marquee';
import { COMPARISON_PILL_ROWS } from './comparisonSectionData';
import styles from './DiagnosticsSignalMarquee.module.css';

const PILL_LABEL_PREFIX = 'comparisonSection.pills';

/** Flatten the comparison pill rows into a single, de-duplicated signal list. */
const SIGNAL_KEYS: ReadonlyArray<{ key: string; default: string }> = (() => {
  const seen = new Set<string>();
  const out: { key: string; default: string }[] = [];
  for (const row of COMPARISON_PILL_ROWS) {
    for (const key of row.pillKeys) {
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ key, default: row.pillDefaults[key] });
    }
  }
  return out;
})();

/**
 * Off-screen, shrink-to-fit copy of a single signal sequence. Rendered only to
 * measure the natural width of one pass so we can repeat it enough times to
 * fill the viewport (no empty gap on wide desktops).
 */

export type SignalMarqueeItem = { key: string; default: string };

type DiagnosticsSignalMarqueeProps = {
  ariaLabel?: string;
  /** Marquee speed in pixels/second. */
  speed?: number;
  /** Pills to render; defaults to the diagnostics signal list. */
  signals?: ReadonlyArray<SignalMarqueeItem>;
  /** i18n key prefix; each label resolves from `${labelPrefix}.${key}.label`. */
  labelPrefix?: string;
};

export const DiagnosticsSignalMarquee: React.FC<
  DiagnosticsSignalMarqueeProps
> = ({
  ariaLabel,
  speed = 30,
  signals = SIGNAL_KEYS,
  labelPrefix = PILL_LABEL_PREFIX,
}) => {
  const t = useTranslations('performance-lab');
  const [play, setPlay] = useState(true);
  const stripRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  // How many times to repeat the signal list so it always spans the container
  // width — a short list would otherwise leave an empty gap on wide screens.
  const [repeatCount, setRepeatCount] = useState(1);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPlay(!media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const labels = useMemo(
    () =>
      signals.map(({ key, default: fallback }) => {
        const fullKey = `${labelPrefix}.${key}.label`;
        return t.has(fullKey) ? t(fullKey) : fallback;
      }),
    [t, signals, labelPrefix],
  );

  // Repeat the sequence enough to fill the viewport; recompute on resize.
  useEffect(() => {
    const computeRepeat = () => {
      const container = stripRef.current;
      const sequence = measureRef.current;
      if (!container || !sequence) return;
      const containerWidth = container.clientWidth;
      const sequenceWidth = sequence.getBoundingClientRect().width;
      if (containerWidth > 0 && sequenceWidth > 0) {
        setRepeatCount(Math.max(1, Math.ceil(containerWidth / sequenceWidth)));
      }
    };
    computeRepeat();
    window.addEventListener('resize', computeRepeat);
    return () => window.removeEventListener('resize', computeRepeat);
  }, [labels]);

  const renderPills = (keyPrefix: string) =>
    labels.map((text, i) => (
      <span className={styles.pill} key={`${keyPrefix}-${text}-${i}`}>
        <span className={styles.divider} aria-hidden />
        <span className={styles.label}>{text}</span>
      </span>
    ));

  const label =
    ariaLabel ?? t('signalMarquee.aria');

  return (
    <section
      className={styles.strip}
      ref={stripRef}
      aria-label={label}
      data-pl-marquee
    >
      <span className={styles.visuallyHidden}>{label}</span>
      <div className={styles.measureRow} ref={measureRef} aria-hidden>
        {renderPills('measure')}
      </div>
      <Marquee gradient={false} speed={speed} play={play} pauseOnHover>
        <div aria-hidden style={{ display: 'flex', alignItems: 'center' }}>
          {Array.from({ length: repeatCount }).map((_, r) => (
            <React.Fragment key={`seq-${r}`}>
              {renderPills(`seq-${r}`)}
            </React.Fragment>
          ))}
        </div>
      </Marquee>
    </section>
  );
};

export default DiagnosticsSignalMarquee;
